/**
 * Typed wrappers for the POSH module API (Phase 3.3a: training +
 * certificates). Paths and shapes are taken verbatim from
 * backend/internal/router/router.go and the training.go /
 * certificates.go handlers.
 *
 * Cookie-only auth + the CSRF interceptor + 401→refresh are handled by
 * the shared axios instance (lib/api/client.ts). A tenant without the
 * "posh" feature is fenced server-side by RequireFeature("posh"); the
 * api client turns the backend's 403 { error: "upgrade_required" } into
 * a /pricing redirect, and other 403s reject normally (caller shows the
 * graceful empty/placeholder state).
 *
 * 3.3b (complaints / ICC / policy / reports) should ADD its wrappers
 * to THIS file (keep one POSH api module) using the same `POSH_BASE`.
 */

import {
  API_BASE_URL,
  CSRF_HEADER,
  api,
  blobErrorToApi,
  currentCSRFToken,
} from "./client";
import { namedBlob } from "@/lib/download-filename";
import type {
  AppealResponse,
  AssignICCRequest,
  SetComplaintRespondentRequest,
  AwarenessEventResponse,
  CommitteeResponse,
  ComplaintDetailResponse,
  ComplaintDocumentResponse,
  ComplaintResponse,
  CreateAwarenessEventRequest,
  CreateICCMemberRequest,
  DecideAppealRequest,
  FileAppealRequest,
  FileComplaintRequest,
  FileComplaintResponse,
  FindingResponse,
  InterimReliefResponse,
  ListAppealsResponse,
  ListAwarenessEventsResponse,
  ListInterimReliefsResponse,
  RecordEmployerActionRequest,
  RecordFindingsRequest,
  RecordInterimReliefRequest,
  RemindTrainingRequest,
  RemindTrainingResponse,
  UpdateInterimReliefRequest,
  GeneratePolicyRequest,
  GenerateReportRequest,
  GetQuizResponse,
  ICCComposition,
  ICCMemberResponse,
  ICCMinutesResponse,
  CreateICCMinutesRequest,
  ListCertificatesResponse,
  ListComplaintsResponse,
  ListICCMembersResponse,
  ListICCMinutesResponse,
  ListModulesResponse,
  ListPoliciesResponse,
  ListPolicyTemplatesResponse,
  ListReportsResponse,
  ListScenariosResponse,
  ListScenesResponse,
  LocalizedText,
  POSHComplianceResponse,
  POSHTrainingSettings,
  UpdatePOSHTrainingSettingsRequest,
  PolicyDetailResponse,
  PolicyResponse,
  PublicVerifyResponse,
  PublishPolicyRequest,
  ReportResponse,
  SubmitQuizRequest,
  SubmitQuizResponse,
  UpdateComplaintStatusRequest,
  UpdateICCMemberRequest,
  UpdatePolicyRequest,
  UpdateProgressRequest,
  UpdateProgressResponse,
  UpdateReportRequest,
  UpdateReportStatusRequest,
} from "@/types/posh";

export const POSH_BASE = "/api/v1/posh";

// ---------------------------------------------------------------------------
// i18n helper — resolve a JSONB i18n bag to a display string.
// ---------------------------------------------------------------------------

/**
 * Resolve a `{"en": "...", "hi": "..."}` bag (or a bare string) to a
 * display string. Prefers `lang`, then English, then the first
 * available value. Returns "" for null/undefined/empty so callers can
 * decide on a neutral fallback rather than rendering "undefined".
 *
 * The POSH training screens pass the person's course language
 * (usePoshLang, lib/posh-lang.ts — Q4 H2); a guard test
 * (lib/posh-lang.test.ts) fails if one of them omits it.
 */
export function localize(value: LocalizedText | undefined | null, lang = "en"): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const v = value as Record<string, string>;
    if (typeof v[lang] === "string" && v[lang]) return v[lang];
    if (typeof v.en === "string" && v.en) return v.en;
    for (const k of Object.keys(v)) {
      if (typeof v[k] === "string" && v[k]) return v[k];
    }
  }
  return "";
}

// ---------------------------------------------------------------------------
// Training (authenticated, RequireFeature("posh"))
// ---------------------------------------------------------------------------

// GET /posh/training/modules
export async function listModules(
  signal?: AbortSignal,
): Promise<ListModulesResponse> {
  const { data } = await api.get<ListModulesResponse>(
    `${POSH_BASE}/training/modules`,
    { signal },
  );
  return data;
}

// GET /posh/training/modules/{moduleId}/scenarios
export async function listScenarios(
  moduleId: number,
  signal?: AbortSignal,
): Promise<ListScenariosResponse> {
  const { data } = await api.get<ListScenariosResponse>(
    `${POSH_BASE}/training/modules/${moduleId}/scenarios`,
    { signal },
  );
  return data;
}

// GET /posh/training/scenarios/{scenarioId}/scenes
export async function listScenes(
  scenarioId: number,
  signal?: AbortSignal,
): Promise<ListScenesResponse> {
  const { data } = await api.get<ListScenesResponse>(
    `${POSH_BASE}/training/scenarios/${scenarioId}/scenes`,
    { signal },
  );
  return data;
}

// GET /posh/training/modules/{moduleId}/quiz — answers stripped server-side.
export async function getQuiz(
  moduleId: number,
  signal?: AbortSignal,
): Promise<GetQuizResponse> {
  const { data } = await api.get<GetQuizResponse>(
    `${POSH_BASE}/training/modules/${moduleId}/quiz`,
    { signal },
  );
  return data;
}

// PUT /posh/training/progress — CSRF header injected by the interceptor.
export async function updateProgress(
  body: UpdateProgressRequest,
): Promise<UpdateProgressResponse> {
  const { data } = await api.put<UpdateProgressResponse>(
    `${POSH_BASE}/training/progress`,
    body,
  );
  return data;
}

/**
 * Time spent, sent while the tab is being closed or hidden (pagehide /
 * visibilitychange). axios cannot outlive the page, so this is a
 * `fetch(..., { keepalive: true })` with the same cookies + CSRF token the
 * shared client would send. Time only — no resume pointer, so the saved
 * position is kept. Fire-and-forget: errors are swallowed (no retry is
 * possible once the page is gone). Returns false when it could not send.
 */
export function sendTimeSpentOnLeave(moduleId: number, seconds: number): boolean {
  if (typeof fetch !== "function") return false;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = currentCSRFToken();
  if (token) headers[CSRF_HEADER] = token;
  const body: UpdateProgressRequest = { module_id: moduleId, time_spent_seconds: seconds };
  try {
    void fetch(`${API_BASE_URL}${POSH_BASE}/training/progress`, {
      method: "PUT",
      keepalive: true,
      credentials: "include",
      headers,
      body: JSON.stringify(body),
    }).catch(() => {});
    return true;
  } catch {
    return false;
  }
}

// POST /posh/training/modules/{moduleId}/quiz/submit — CSRF + Audit.
export async function submitQuiz(
  moduleId: number,
  body: SubmitQuizRequest,
): Promise<SubmitQuizResponse> {
  const { data } = await api.post<SubmitQuizResponse>(
    `${POSH_BASE}/training/modules/${moduleId}/quiz/submit`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Compliance dashboard (business review P1.2)
//
// GET /posh/training/compliance — auth + RequireFeature("posh") +
// RBAC company_admin|hr_manager + Audit. GET, so NO CSRF. A non-admin /
// non-posh tenant is 403'd by the middleware chain (the page also hard
// role-gates so this is never fired for a non-admin). The shared client
// handles cookies/refresh and turns the upgrade_required 403 into the
// /pricing redirect; any other 403 rejects normally so the page can show
// the graceful "no access" state (defence in depth).
// ---------------------------------------------------------------------------

// GET /posh/training/compliance — org-wide training/certification roster.
export async function getCompliance(
  signal?: AbortSignal,
): Promise<POSHComplianceResponse> {
  const { data } = await api.get<POSHComplianceResponse>(
    `${POSH_BASE}/training/compliance`,
    { signal },
  );
  return data;
}

/**
 * POST /posh/training/remind — nudge non-compliant employees to finish
 * mandatory training. HR-admin gated + CSRF; body is { user_id } for one
 * employee or { all: true } for every non-compliant one. The server
 * dedups within 24h and reports both counts back.
 */
export async function remindTraining(
  body: RemindTrainingRequest,
): Promise<RemindTrainingResponse> {
  const { data } = await api.post<RemindTrainingResponse>(
    `${POSH_BASE}/training/remind`,
    body,
  );
  return data;
}

/**
 * GET /posh/training/settings — the annual refresher period (migration
 * 125). Same HR-admin RBAC + posh gate as the compliance roster.
 */
export async function getTrainingSettings(
  signal?: AbortSignal,
): Promise<POSHTrainingSettings> {
  const { data } = await api.get<POSHTrainingSettings>(
    `${POSH_BASE}/training/settings`,
    { signal },
  );
  return data;
}

/**
 * PATCH /posh/training/settings — change the refresher period (0 = off,
 * else 6–36 months). HR-admin + CSRF + audit server-side. A change
 * restarts the 60-day notice period for completions without a printed
 * expiry.
 */
export async function updateTrainingSettings(
  body: UpdatePOSHTrainingSettingsRequest,
): Promise<POSHTrainingSettings> {
  const { data } = await api.patch<POSHTrainingSettings>(
    `${POSH_BASE}/training/settings`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Certificates (authenticated, RequireFeature("posh"))
// ---------------------------------------------------------------------------

// GET /posh/certificates — the caller's own certs (tenant + user scoped).
export async function listCertificates(
  signal?: AbortSignal,
): Promise<ListCertificatesResponse> {
  const { data } = await api.get<ListCertificatesResponse>(
    `${POSH_BASE}/certificates`,
    { signal },
  );
  return data;
}

/**
 * GET /posh/certificates/{id}/pdf — binary PDF for an OWNED cert.
 * 410 Gone if the cert is revoked (backend fails closed so a revoked
 * cert can never be passed off as valid). Returns the Blob so the
 * caller can trigger a download without leaving the SPA.
 */
export async function downloadCertificatePdf(id: number): Promise<Blob> {
  let res;
  try {
    res = await api.get<Blob>(`${POSH_BASE}/certificates/${id}/pdf`, {
      responseType: "blob",
    });
  } catch (err) {
    // A JSON error (410 certificate_revoked) arrives as a Blob — rehydrate
    // so toApiError() reads its code.
    throw await blobErrorToApi(err);
  }
  // Carry the server's Content-Disposition filename (saveBlobAs uses it).
  return namedBlob(res.data, res.headers?.["content-disposition"]);
}

// ---------------------------------------------------------------------------
// Public verification (NO auth / CSRF / tenant) — server-side only.
// ---------------------------------------------------------------------------

/**
 * Fetch the PUBLIC verification result for a cert_uid. Used by the
 * server component at /verify/[certId] — it deliberately does NOT use
 * the axios client (no cookies/CSRF needed and the verify page is
 * server-rendered for shareability). Mirrors the backend exactly:
 *  - 404 → { valid: false }  (unknown uid)
 *  - 200 { valid: false }    (revoked — PII-identical to unknown)
 *  - 200 { valid: true, ... }(valid: minimal non-PII display fields)
 * Any network/parse failure resolves to { valid: false } so the page
 * never leaks an error and never implies a cert exists.
 */
export async function fetchPublicVerification(
  certUid: string,
): Promise<PublicVerifyResponse> {
  // Migration note (2026-06-01): the Next.js sibling reads
  // process.env.NEXT_PUBLIC_API_URL (the /verify/[certId] page is
  // server-rendered there for shareability). The Vite SPA has no
  // server runtime — this is a client fetch reachable from a public
  // route. Source the origin from import.meta.env.VITE_API_URL (baked
  // at vite build time), same localhost fallback as the Next.js client.
  const base = (
    import.meta.env.VITE_API_URL?.replace(/\/$/, "") ??
    "http://localhost:8090"
  );
  try {
    const res = await fetch(
      `${base}${POSH_BASE}/verify/certificates/${encodeURIComponent(certUid)}`,
      { cache: "no-store" },
    );
    // 404 (unknown) and 200 (valid/revoked) both carry a JSON body whose
    // PII content is identical for the unknown/revoked cases. We never
    // branch UI on the status — only on `valid`.
    const data = (await res.json()) as PublicVerifyResponse;
    if (data && typeof data.valid === "boolean") return data;
    return { valid: false };
  } catch {
    // Network/parse error: behave exactly like "not valid" — never
    // surface an error that could imply the cert exists.
    return { valid: false };
  }
}

// ===========================================================================
// Phase 3.3b — Complaints / ICC / Policy / Annual reports
//
// All authenticated + RequireFeature("posh"). The shared client handles
// cookies, the CSRF header on writes, and the 401→refresh / 403
// upgrade_required→/pricing flow. STRICT-2FA routes (the ICC/admin
// complaint reads + every /icc route) 403 a non-TOTP-enrolled caller —
// callers must surface that as a "2FA required" state, NOT a generic
// error (see hooks/use-posh.ts + the page components). The own-complaint
// flow (file + /my-complaints) is NOT 2FA-gated.
// ===========================================================================

// --- Complaints: own (no 2FA) ------------------------------------------

// POST /posh/complaints — file a complaint (complainant = authed user).
export async function fileComplaint(
  body: FileComplaintRequest,
): Promise<FileComplaintResponse> {
  const { data } = await api.post<FileComplaintResponse>(
    `${POSH_BASE}/complaints`,
    body,
  );
  return data;
}

// GET /posh/my-complaints — the caller's OWN complaints (always unmasked
// to the owner). Optional status filter.
export async function listMyComplaints(
  params?: { status?: string; limit?: number; offset?: number },
  signal?: AbortSignal,
): Promise<ListComplaintsResponse> {
  const { data } = await api.get<ListComplaintsResponse>(
    `${POSH_BASE}/my-complaints`,
    { params, signal },
  );
  return data;
}

// GET /posh/my-complaints/{id} — own complaint detail + timeline + docs.
export async function getMyComplaint(
  id: number,
  signal?: AbortSignal,
): Promise<ComplaintDetailResponse> {
  const { data } = await api.get<ComplaintDetailResponse>(
    `${POSH_BASE}/my-complaints/${id}`,
    { signal },
  );
  return data;
}

// POST /posh/my-complaints/{id}/documents — the complainant attaches
// evidence to THEIR OWN complaint (no 2FA; the server records it as
// "evidence" and 404s anyone who did not file the complaint). FormData —
// the browser sets the multipart boundary; the client adds CSRF.
export async function uploadMyComplaintDocument(
  complaintId: number,
  file: File,
): Promise<ComplaintDocumentResponse> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post<ComplaintDocumentResponse>(
    `${POSH_BASE}/my-complaints/${complaintId}/documents`,
    form,
  );
  return data;
}

// GET /posh/my-complaints/{id}/documents/{documentId} — the complainant
// downloads a document on their own complaint (no 2FA).
export async function downloadMyComplaintDocument(
  complaintId: number,
  documentId: number,
): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `${POSH_BASE}/my-complaints/${complaintId}/documents/${documentId}`,
      { responseType: "blob" },
    );
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

// GET /posh/committee — who is on the Internal Committee today (names +
// committee role only) and whether the caller can see the committee
// register. Every employee; no 2FA.
export async function getCommittee(
  signal?: AbortSignal,
): Promise<CommitteeResponse> {
  const { data } = await api.get<CommitteeResponse>(
    `${POSH_BASE}/committee`,
    { signal },
  );
  return data;
}

// --- Complaints: ICC/admin (STRICT 2FA — a non-enrolled user is 403'd) -

// GET /posh/complaints — register visible to ICC/admin per server-side
// row visibility. The ANONYMITY rule is applied server-side: an
// anonymous complaint's complainant_id is OMITTED for non-owners and
// is_complainant_masked is true.
export async function listComplaints(
  params?: { status?: string; limit?: number; offset?: number },
  signal?: AbortSignal,
): Promise<ListComplaintsResponse> {
  const { data } = await api.get<ListComplaintsResponse>(
    `${POSH_BASE}/complaints`,
    { params, signal },
  );
  return data;
}

// GET /posh/complaints/{id} — ICC/admin detail + timeline + documents.
export async function getComplaint(
  id: number,
  signal?: AbortSignal,
): Promise<ComplaintDetailResponse> {
  const { data } = await api.get<ComplaintDetailResponse>(
    `${POSH_BASE}/complaints/${id}`,
    { signal },
  );
  return data;
}

// PATCH /posh/complaints/{id}/status — validated state-machine move.
export async function updateComplaintStatus(
  id: number,
  body: UpdateComplaintStatusRequest,
): Promise<ComplaintResponse> {
  const { data } = await api.patch<ComplaintResponse>(
    `${POSH_BASE}/complaints/${id}/status`,
    body,
  );
  return data;
}

// POST /posh/complaints/{id}/assign — assign an active ICC member.
export async function assignComplaintICC(
  id: number,
  body: AssignICCRequest,
): Promise<ComplaintResponse> {
  const { data } = await api.post<ComplaintResponse>(
    `${POSH_BASE}/complaints/${id}/assign`,
    body,
  );
  return data;
}

// PUT /posh/complaints/{id}/respondent — link (or, with null, unlink) the
// respondent's account. Internal Committee case-file only (server-checked);
// once linked the respondent can never read or handle the case.
export async function setComplaintRespondent(
  id: number,
  body: SetComplaintRespondentRequest,
): Promise<ComplaintResponse> {
  const { data } = await api.put<ComplaintResponse>(
    `${POSH_BASE}/complaints/${id}/respondent`,
    body,
  );
  return data;
}

// POST /posh/complaints/{id}/documents — multipart evidence upload. The
// shared client still injects the CSRF header; we let the browser set
// the multipart boundary by passing FormData (do NOT set Content-Type).
export async function uploadComplaintDocument(
  id: number,
  file: File,
  opts?: { documentType?: string; description?: string },
): Promise<ComplaintDocumentResponse> {
  const form = new FormData();
  form.append("file", file);
  if (opts?.documentType) form.append("document_type", opts.documentType);
  if (opts?.description) form.append("description", opts.description);
  const { data } = await api.post<ComplaintDocumentResponse>(
    `${POSH_BASE}/complaints/${id}/documents`,
    form,
  );
  return data;
}

/**
 * GET /posh/complaints/{id}/documents/{documentId} — authed blob, the
 * committee's (strict-2FA) route. Non-2xx (403/404) rejects via the
 * client; the caller toasts without leaking. The complainant's own
 * download is downloadMyComplaintDocument (no 2FA, 2026-10).
 */
export async function downloadComplaintDocument(
  complaintId: number,
  documentId: number,
): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `${POSH_BASE}/complaints/${complaintId}/documents/${documentId}`,
      { responseType: "blob" },
    );
    return data;
  } catch (err) {
    // Same blob-response pitfall as the HR document download: the JSON
    // error envelope arrives as an unparsed Blob. Reuse the SHARED
    // rehydrator so the two paths can't diverge; the 403/404 still
    // surfaces only the backend's neutral message (no file-exists
    // oracle).
    throw await blobErrorToApi(err);
  }
}

// --- Inquiry lifecycle: §12 interim reliefs / §13 findings + §13(4)
//     employer action / §18 appeals (migration 090, STRICT 2FA — a
//     non-enrolled caller is 403'd; the manage page surfaces that state).
//     Every mutation is CSRF-guarded by the shared client interceptor.

// POST /posh/complaints/{id}/interim-relief — recommend a §12 relief.
export async function recordInterimRelief(
  complaintId: number,
  body: RecordInterimReliefRequest,
): Promise<InterimReliefResponse> {
  const { data } = await api.post<InterimReliefResponse>(
    `${POSH_BASE}/complaints/${complaintId}/interim-relief`,
    body,
  );
  return data;
}

// PATCH /posh/complaints/{id}/interim-relief/{reliefId} — update status/notes.
export async function updateInterimRelief(
  complaintId: number,
  reliefId: number,
  body: UpdateInterimReliefRequest,
): Promise<InterimReliefResponse> {
  const { data } = await api.patch<InterimReliefResponse>(
    `${POSH_BASE}/complaints/${complaintId}/interim-relief/${reliefId}`,
    body,
  );
  return data;
}

// GET /posh/complaints/{id}/interim-relief — a complaint's §12 reliefs.
export async function listInterimReliefs(
  complaintId: number,
  signal?: AbortSignal,
): Promise<ListInterimReliefsResponse> {
  const { data } = await api.get<ListInterimReliefsResponse>(
    `${POSH_BASE}/complaints/${complaintId}/interim-relief`,
    { signal },
  );
  return data;
}

// POST /posh/complaints/{id}/findings — record the §13 inquiry report.
export async function recordFindings(
  complaintId: number,
  body: RecordFindingsRequest,
): Promise<FindingResponse> {
  const { data } = await api.post<FindingResponse>(
    `${POSH_BASE}/complaints/${complaintId}/findings`,
    body,
  );
  return data;
}

// PATCH /posh/complaints/{id}/action — record §13(4) employer action.
export async function recordEmployerAction(
  complaintId: number,
  body: RecordEmployerActionRequest,
): Promise<FindingResponse> {
  const { data } = await api.patch<FindingResponse>(
    `${POSH_BASE}/complaints/${complaintId}/action`,
    body,
  );
  return data;
}

// POST /posh/complaints/{id}/appeals — file a §18 appeal.
export async function fileAppeal(
  complaintId: number,
  body: FileAppealRequest,
): Promise<AppealResponse> {
  const { data } = await api.post<AppealResponse>(
    `${POSH_BASE}/complaints/${complaintId}/appeals`,
    body,
  );
  return data;
}

// PATCH /posh/complaints/{id}/appeals/{appealId} — validated status move.
export async function decideAppeal(
  complaintId: number,
  appealId: number,
  body: DecideAppealRequest,
): Promise<AppealResponse> {
  const { data } = await api.patch<AppealResponse>(
    `${POSH_BASE}/complaints/${complaintId}/appeals/${appealId}`,
    body,
  );
  return data;
}

// GET /posh/complaints/{id}/appeals — a complaint's §18 appeals.
export async function listAppeals(
  complaintId: number,
  signal?: AbortSignal,
): Promise<ListAppealsResponse> {
  const { data } = await api.get<ListAppealsResponse>(
    `${POSH_BASE}/complaints/${complaintId}/appeals`,
    { signal },
  );
  return data;
}

// --- ICC members (STRICT 2FA on every route) ---------------------------

// GET /posh/icc/members
export async function listICCMembers(
  signal?: AbortSignal,
): Promise<ListICCMembersResponse> {
  const { data } = await api.get<ListICCMembersResponse>(
    `${POSH_BASE}/icc/members`,
    { signal },
  );
  return data;
}

// POST /posh/icc/members
export async function createICCMember(
  body: CreateICCMemberRequest,
): Promise<ICCMemberResponse> {
  const { data } = await api.post<ICCMemberResponse>(
    `${POSH_BASE}/icc/members`,
    body,
  );
  return data;
}

// PATCH /posh/icc/members/{id}
export async function updateICCMember(
  id: number,
  body: UpdateICCMemberRequest,
): Promise<ICCMemberResponse> {
  const { data } = await api.patch<ICCMemberResponse>(
    `${POSH_BASE}/icc/members/${id}`,
    body,
  );
  return data;
}

// DELETE /posh/icc/members/{id} — deactivate (seat preserved for record).
export async function deactivateICCMember(id: number): Promise<void> {
  await api.delete(`${POSH_BASE}/icc/members/${id}`);
}

// GET /posh/icc/composition — POSH Act §4 composition health snapshot.
//
// Auth + RequireFeature("posh") + RBAC(company_admin, hr_manager); NOT
// behind the strict-2FA gate (carries no complaint data — see
// handler doc in icc.go::Composition). The shared client handles
// cookies / refresh / upgrade_required→/pricing; any other 403 (e.g. a
// non-admin) rejects normally and the page renders the "no access"
// state.
export async function getICCComposition(
  signal?: AbortSignal,
): Promise<ICCComposition> {
  const { data } = await api.get<ICCComposition>(
    `${POSH_BASE}/icc/composition`,
    { signal },
  );
  return data;
}

// --- IC meeting minutes (CR1.2, STRICT 2FA on every route) -------------

// GET /posh/icc/minutes — a tenant's minutes, newest first. An optional
// complaintId narrows to one inquiry's minutes.
export async function listICCMinutes(
  complaintId?: number,
  signal?: AbortSignal,
): Promise<ListICCMinutesResponse> {
  const { data } = await api.get<ListICCMinutesResponse>(
    `${POSH_BASE}/icc/minutes`,
    {
      params:
        complaintId != null ? { complaint_id: complaintId } : undefined,
      signal,
    },
  );
  return data;
}

// POST /posh/icc/minutes — record an immutable minutes row. Quorum (Rule 7)
// is computed server-side from the attendees + PO presence.
export async function createICCMinutes(
  body: CreateICCMinutesRequest,
): Promise<ICCMinutesResponse> {
  const { data } = await api.post<ICCMinutesResponse>(
    `${POSH_BASE}/icc/minutes`,
    body,
  );
  return data;
}

// --- Policy (RBAC company_admin/hr_manager on mutations; NOT 2FA-gated) -

// GET /posh/policy/templates — GLOBAL platform boilerplate (read-only).
export async function listPolicyTemplates(
  signal?: AbortSignal,
): Promise<ListPolicyTemplatesResponse> {
  const { data } = await api.get<ListPolicyTemplatesResponse>(
    `${POSH_BASE}/policy/templates`,
    { signal },
  );
  return data;
}

// GET /posh/policy/policies — the tenant's own policies.
export async function listPolicies(
  params?: { status?: string },
  signal?: AbortSignal,
): Promise<ListPoliciesResponse> {
  const { data } = await api.get<ListPoliciesResponse>(
    `${POSH_BASE}/policy/policies`,
    { params, signal },
  );
  return data;
}

// GET /posh/policy/policies/{id} — policy + immutable version snapshots.
export async function getPolicy(
  id: number,
  signal?: AbortSignal,
): Promise<PolicyDetailResponse> {
  const { data } = await api.get<PolicyDetailResponse>(
    `${POSH_BASE}/policy/policies/${id}`,
    { signal },
  );
  return data;
}

// POST /posh/policy/policies — generate a tenant policy from a template.
export async function generatePolicy(
  body: GeneratePolicyRequest,
): Promise<PolicyResponse> {
  const { data } = await api.post<PolicyResponse>(
    `${POSH_BASE}/policy/policies`,
    body,
  );
  return data;
}

// PATCH /posh/policy/policies/{id} — edit a DRAFT policy in place.
export async function updatePolicy(
  id: number,
  body: UpdatePolicyRequest,
): Promise<PolicyResponse> {
  const { data } = await api.patch<PolicyResponse>(
    `${POSH_BASE}/policy/policies/${id}`,
    body,
  );
  return data;
}

// POST /posh/policy/policies/{id}/publish — freeze a version + publish.
export async function publishPolicy(
  id: number,
  body?: PublishPolicyRequest,
): Promise<PolicyDetailResponse> {
  const { data } = await api.post<PolicyDetailResponse>(
    `${POSH_BASE}/policy/policies/${id}/publish`,
    body ?? {},
  );
  return data;
}

/**
 * GET /posh/policy/policies/{id}/pdf[?version=<id>] — policy PDF blob.
 * No ?version → the live policy; ?version=<id> → that immutable
 * published snapshot. Non-2xx rejects via the client.
 */
export async function downloadPolicyPdf(
  id: number,
  versionId?: number,
): Promise<Blob> {
  const { data } = await api.get<Blob>(
    `${POSH_BASE}/policy/policies/${id}/pdf`,
    {
      responseType: "blob",
      params: versionId ? { version: versionId } : undefined,
    },
  );
  return data;
}

// --- Annual reports (RBAC company_admin/hr_manager on mutations) --------

// GET /posh/reports — the tenant's annual reports.
export async function listReports(
  params?: { status?: string },
  signal?: AbortSignal,
): Promise<ListReportsResponse> {
  const { data } = await api.get<ListReportsResponse>(
    `${POSH_BASE}/reports`,
    { params, signal },
  );
  return data;
}

// GET /posh/reports/{id}
export async function getReport(
  id: number,
  signal?: AbortSignal,
): Promise<ReportResponse> {
  const { data } = await api.get<ReportResponse>(
    `${POSH_BASE}/reports/${id}`,
    { signal },
  );
  return data;
}

// POST /posh/reports — generate the counts-only annual report for a year.
export async function generateReport(
  body: GenerateReportRequest,
): Promise<ReportResponse> {
  const { data } = await api.post<ReportResponse>(
    `${POSH_BASE}/reports`,
    body,
  );
  return data;
}

// PATCH /posh/reports/{id} — edit notes on a not-yet-filed report.
export async function updateReport(
  id: number,
  body: UpdateReportRequest,
): Promise<ReportResponse> {
  const { data } = await api.patch<ReportResponse>(
    `${POSH_BASE}/reports/${id}`,
    body,
  );
  return data;
}

// PATCH /posh/reports/{id}/status — validated state-machine move.
export async function updateReportStatus(
  id: number,
  body: UpdateReportStatusRequest,
): Promise<ReportResponse> {
  const { data } = await api.patch<ReportResponse>(
    `${POSH_BASE}/reports/${id}/status`,
    body,
  );
  return data;
}

// GET /posh/reports/{id}/pdf — the annual-report PDF (counts only) blob.
export async function downloadReportPdf(id: number): Promise<Blob> {
  const { data } = await api.get<Blob>(
    `${POSH_BASE}/reports/${id}/pdf`,
    { responseType: "blob" },
  );
  return data;
}

// --- CR0.1 export pack + §19(c) awareness event register ----------------

/**
 * GET /posh/reports/{id}/shebox.csv — the Rule 14 / SHe-Box submission
 * pack (report counts + ICC registration details). STRICT 2FA + HR-admin
 * gated server-side (it carries the ICC member registry): a non-enrolled
 * admin rejects with totp_required — the shared client interceptor
 * handles that; other errors rehydrate via blobErrorToApi.
 */
export async function downloadReportSheBoxCsv(id: number): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `${POSH_BASE}/reports/${id}/shebox.csv`,
      { responseType: "blob" },
    );
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/**
 * GET /posh/reports/{id}/board-annexure.csv — Companies (Accounts) Rules
 * Rule 8(5)(x)+(xi) board-report annexure draft (counts + compliance
 * statements only). HR-admin gated server-side.
 */
export async function downloadReportBoardAnnexureCsv(
  id: number,
): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `${POSH_BASE}/reports/${id}/board-annexure.csv`,
      { responseType: "blob" },
    );
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

// GET /posh/awareness-events — the §19(c) workshop register (HR-admin).
export async function listAwarenessEvents(
  signal?: AbortSignal,
): Promise<ListAwarenessEventsResponse> {
  const { data } = await api.get<ListAwarenessEventsResponse>(
    `${POSH_BASE}/awareness-events`,
    { signal },
  );
  return data;
}

// POST /posh/awareness-events — record a conducted workshop/programme.
export async function createAwarenessEvent(
  body: CreateAwarenessEventRequest,
): Promise<AwarenessEventResponse> {
  const { data } = await api.post<AwarenessEventResponse>(
    `${POSH_BASE}/awareness-events`,
    body,
  );
  return data;
}

// DELETE /posh/awareness-events/{id}.
export async function deleteAwarenessEvent(id: number): Promise<void> {
  await api.delete(`${POSH_BASE}/awareness-events/${id}`);
}
